// v2 meerkat — the full sentry pose: a slim body standing tall on two short hind
// legs and flat grounded feet, braced from behind by its thin dark-tipped tail
// (the real tripod stance), little arms folded over a pale belly, faint dark
// back bands, and the bandit face — dark eye patches, low round ears and a
// pointed snout with a dark nose. Two coats: warm sand and grizzled grey. Ported
// 1:1 from age-of-mcp's v2/meerkat.ts, using the library's shared `wildland`
// (tube) helper.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const COATS = [
  { fur: "#c5a570", belly: "#e2cb9c", face: "#cdb080", dark: "#3a2c1c", band: "#9c8254" },
  { fur: "#b3a07c", belly: "#d6c8a8", face: "#bfae8a", dark: "#322a1e", band: "#8d7d5e" },
];

function meerkatParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = [
    // haunches over the hind legs + slim upright torso + pale belly
    { geometry: new THREE.IcosahedronGeometry(0.0165, 0), color: c.fur,
      position: [0, 0.03, 0], scale: [1.0, 1.1, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.0175, 0), color: c.fur,
      position: [0.004, 0.062, 0], scale: [0.85, 1.55, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: c.belly,
      position: [0.014, 0.058, 0], scale: [0.6, 1.35, 0.7] },
    // head + pointed snout + dark nose
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.face,
      position: [0.008, 0.102, 0], scale: [1.05, 1.0, 0.95] },
    { geometry: new THREE.ConeGeometry(0.006, 0.016, 4), color: c.face,
      position: [0.024, 0.098, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0025, 0), color: c.dark,
      position: [0.0325, 0.098, 0] },
    // the tail tripod — two segments braced on the ground, dark tip
    wildland.tube([-0.012, 0.025, 0], [-0.026, 0.008, 0.004], 0.004, 0.0032, c.fur),
    wildland.tube([-0.026, 0.008, 0.004], [-0.038, 0.0015, 0.008], 0.0032, 0.0022, c.fur),
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: c.dark,
      position: [-0.043, 0.002, 0.0095] },
  ];
  // faint dark bands across the lower back
  for (const by of [0.075, 0.062, 0.049]) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.0045, 0),
      color: c.band, position: [-0.0095, by, 0], scale: [0.5, 0.5, 1.6] });
  }
  for (const s of [1, -1] as const) {
    parts.push(
      // short hind leg + flat grounded foot
      wildland.tube([0.002, 0.03, s * 0.0105], [0.005, 0.006, s * 0.011],
        0.0055, 0.004, c.fur),
      { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: c.fur,
        position: [0.006, 0.0045, s * 0.011], scale: [1.6, 0.5, 0.9] },
      // little arms folded in front + dark paws
      wildland.tube([0.012, 0.075, s * 0.009], [0.022, 0.062, s * 0.0055],
        0.0035, 0.0028, c.fur),
      { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: c.dark,
        position: [0.0235, 0.06, s * 0.005] },
      // bandit eye patches + low round ears
      { geometry: new THREE.IcosahedronGeometry(0.0042, 0), color: c.dark,
        position: [0.0175, 0.107, s * 0.0075], scale: [0.8, 1.1, 0.9] },
      { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: c.dark,
        position: [0.002, 0.112, s * 0.0135] },
    );
  }
  return parts;
}

export const meerkatV2 = defineModel({
  id: "meerkat-v2",
  batchId: "fable",
  name: "Meerkat (v2)",
  tags: ["creature","meerkat","low-poly","age-of-mcp","fable"],
  summary: "Low-poly meerkat.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => meerkatParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => meerkatParts(1) },
  ],
  animations: [],
});
